import { useCallback } from 'react';
import { useAuth } from '@/shared/providers/AuthContext';
import useAppToast from '@/hooks/useAppToast';
import { useAddressSearch } from '@/hooks/useAddressSearch';

export interface AddressLookupValues {
  pc1: string;
  pc2: string;
  address: string;
}

export interface UseAddressLookupOptions {
  /** Read current postal/address values from the form. */
  getValues: () => AddressLookupValues;
  /** Apply a single-result match back into the form. */
  applyResult: (result: AddressLookupValues) => void;
  /** Open the AddressSearchModal when search is ambiguous / empty. */
  openModal: () => void;
  isAdmin?: boolean;
  /** Show warning toast when user supplied a full postal/address but API found nothing. Default true. */
  showNotFoundWarning?: boolean;
  /** Called after a single result is applied — use to focus the address field and move cursor to end. */
  onAfterApply?: () => void;
}

const NOT_FOUND_MESSAGE = '該当する住所がありませんでした。もう一度入力しなおしてください。';

const formatFullAddress = (r: any): string =>
  r.full_address ||
  `${r.prefecture_name || ''}${r.city_name || ''}${r.town_name || r.name || ''}`;

/**
 * Unified 住所補助 search flow — mirrors the logic previously duplicated across
 * TreatmentReport / Consent / HospitalModal / PatientAddress forms.
 *
 *   pc1  pc2  addr  → behavior
 *   -    -    -     → open modal
 *   -    y    -     → open modal
 *   -    y    y     → search by address only (fill pc1/pc2 from result)
 *   y    *    *     → search by postal (+ address if present)
 *
 *   0 results + full input → warning toast (unless showNotFoundWarning=false)
 *   0 results + partial    → open modal
 *   1 result               → applyResult
 *   many results           → open modal
 */
export const useAddressLookup = ({
  getValues,
  applyResult,
  openModal,
  isAdmin = false,
  showNotFoundWarning = true,
  onAfterApply,
}: UseAddressLookupOptions) => {
  const { clinic } = useAuth();
  const mutation = useAddressSearch({
    siteType: clinic?.site_type || 'medix',
    clinicNumber: clinic?.clinic_number || '',
    isAdmin,
  });
  const toast = useAppToast();

  const search = useCallback(() => {
    if (mutation.isPending) return;

    const raw = getValues();
    const pc1 = (raw.pc1 || '').trim();
    const pc2 = (raw.pc2 || '').trim();
    const addr = (raw.address || '').trim();

    if (!pc1 && !pc2 && !addr) {
      openModal();
      return;
    }
    if (!pc1 && pc2 && !addr) {
      openModal();
      return;
    }

    const prioritizeAddress = !pc1 && !!pc2 && !!addr;
    const payload: { postal_code1?: string; postal_code2?: string; address?: string } = {};
    if (prioritizeAddress) {
      payload.address = addr;
    } else {
      if (pc1) payload.postal_code1 = pc1;
      if (pc2) payload.postal_code2 = pc2;
      if (addr) payload.address = addr;
    }

    const isFullPostal = pc1.length === 3 && pc2.length === 4;
    const isFullAddress = (!!addr && !pc1 && !pc2) || prioritizeAddress;

    mutation.mutate(payload, {
      onSuccess: (results: any[]) => {
        if (results.length === 0) {
          if (showNotFoundWarning && (isFullPostal || isFullAddress)) {
            toast.warning(NOT_FOUND_MESSAGE);
            return;
          }
          openModal();
        } else if (results.length === 1) {
          const r = results[0];
          applyResult({
            pc1: r.postal_code1 || '',
            pc2: r.postal_code2 || '',
            address: formatFullAddress(r),
          });
          if (onAfterApply) setTimeout(onAfterApply, 0);
        } else {
          openModal();
        }
      },
      onError: () => openModal(),
    });
  }, [mutation, getValues, applyResult, openModal, showNotFoundWarning, onAfterApply, toast]);

  return { search, isPending: mutation.isPending };
};

export default useAddressLookup;
